import { CurrencyPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { MbaIcon } from '../../assignment/atoms/mba-icon/mba-icon';
import { briefKind, payView, sectionStatus } from '../briefing.helpers';
import { BriefingService } from '../briefing.service';
import { bf } from '../briefing-ui';
import { BriefingSection } from '../molecules/briefing-section/briefing-section';
import { BriefingVoucherCodes } from '../molecules/briefing-voucher-codes/briefing-voucher-codes';

/**
 * Pay, read-only. Cash briefs show price × videos and, on Influence briefs, the audience bonus
 * tiers. A piece brief can price videos differently, so it shows a range and a per-video
 * breakdown. SHOUT+ voucher briefs keep the voucher code manager.
 */
@Component({
  selector: 'mbt-briefing-pay',
  imports: [BriefingSection, MbaIcon, CurrencyPipe, BriefingVoucherCodes],
  template: `
    @if (svc.brief(); as brief) {
    <mbt-briefing-section key="comp" icon="banknote" title="Pay" [flash]="flash()" [editable]="false"
      [helper]="influence() && kind() === 'cash' ? 'What each creator earns for completing the brief. Creators with bigger audiences earn more.' : 'What each creator earns for completing the brief.'"
      [status]="status()">
      @switch (kind()) {
        @case ('voucher') {
          <mbt-briefing-voucher-codes [paymentMethodId]="brief.brief_payment_method_id!" />
        }
        @case ('affiliate') {
          <div [class]="bf.info"><mba-icon name="wallet" size="size-4" /><span>Creators earn affiliate commission on the sales they drive.</span></div>
        }
        @case ('plus') {
          <div [class]="bf.info"><mba-icon name="wallet" size="size-4" /><span>Creators are rewarded through SHOUT PLUS.</span></div>
        }
        @default {
          <div class="flex flex-wrap items-stretch gap-2.5">
            <div class="flex flex-col gap-1 rounded-lg border border-(--border-subtle) px-4 py-3">
              <span [class]="bf.k">{{ influence() ? 'Base price per video' : 'Price per video' }}</span>
              <b [class]="num">{{ pay().baseMin | currency: pay().currency : 'symbol' : '1.0-2' }}@if (pay().baseMax !== pay().baseMin) {–{{ pay().baseMax | currency: pay().currency : 'symbol' : '1.0-2' }}}</b>
            </div>
            <span class="grid place-items-center font-(family-name:--font-display) text-2xl font-bold text-(--fg-3)" aria-hidden="true">×</span>
            <div class="flex flex-col gap-1 rounded-lg border border-(--border-subtle) px-4 py-3">
              <span [class]="bf.k">Videos</span>
              <b [class]="num">{{ pay().videos }}</b>
            </div>
            <span class="grid place-items-center font-(family-name:--font-display) text-2xl font-bold text-(--fg-3)" aria-hidden="true">=</span>
            <div class="flex min-w-[200px] flex-1 flex-col gap-1 rounded-lg bg-(--shout-deep-purple) px-4 py-3 text-white">
              <span class="text-[11px] font-bold uppercase tracking-[0.08em] text-white/70">Each creator earns{{ pay().tiers.length ? ' at least' : '' }}</span>
              <b class="font-(family-name:--font-display) text-[30px] leading-none font-extrabold tabular-nums">{{ pay().total | currency: pay().currency : 'symbol' : '1.0-2' }}</b>
              @if (pay().tiers.length) { <span class="text-xs text-white/70">Bigger audiences earn more, see below</span> }
            </div>
          </div>

          @if (pay().baseMax !== pay().baseMin) {
            <ul class="m-0 flex list-none flex-col gap-1 p-0 text-[13px] text-(--fg-2)">
              @for (video of pay().perVideo; track $index) {
                <li class="flex gap-2"><span class="flex-1">{{ video.label }}</span><b class="tabular-nums text-(--fg-1)">{{ video.amount | currency: pay().currency : 'symbol' : '1.0-2' }}</b></li>
              }
            </ul>
          }

          @if (pay().tiers.length) {
            <div class="overflow-x-auto rounded-lg border border-(--border-subtle)">
              <table class="w-full border-collapse text-sm">
                <thead>
                  <tr class="bg-(--bg-alt) text-left">
                    <th scope="col" [class]="th">Audience size</th>
                    <th scope="col" [class]="th">Followers</th>
                    <th scope="col" [class]="th + ' text-right'">Per video</th>
                    <th scope="col" [class]="th + ' text-right'">Creator earns</th>
                  </tr>
                </thead>
                <tbody>
                  @for (tier of pay().tiers; track tier.title) {
                    <tr class="border-t border-(--border-subtle)">
                      <td class="px-4 py-3">
                        <span class="flex items-center gap-2.5">
                          <span class="flex items-end gap-[2px]" aria-hidden="true">
                            @for (bar of bars; track bar) {
                              <span [class]="'w-1 rounded-[1px] ' + (bar <= tier.bars ? 'bg-(--app-accent)' : 'bg-(--border-default)')" [style.height.px]="4 + bar * 3"></span>
                            }
                          </span>
                          <b class="font-(family-name:--font-display) text-lg leading-none font-extrabold">{{ tier.title }}</b>
                        </span>
                      </td>
                      <td class="px-4 py-3 text-(--fg-2)">{{ tier.followers }}</td>
                      <td class="px-4 py-3 text-right tabular-nums">
                        {{ pay().baseMin | currency: pay().currency : 'symbol' : '1.0-2' }}@if (pay().baseMax !== pay().baseMin) {–{{ pay().baseMax | currency: pay().currency : 'symbol' : '1.0-2' }}}
                        @if (tier.bonus) { <span class="ml-1.5 font-bold text-(--success)">+ {{ tier.bonus | currency: pay().currency : 'symbol' : '1.0-2' }}</span> }
                      </td>
                      <td class="px-4 py-3 text-right"><b class="font-(family-name:--font-display) text-lg font-extrabold tabular-nums">{{ tier.earns | currency: pay().currency : 'symbol' : '1.0-2' }}</b></td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }

          <div [class]="bf.info"><mba-icon name="wallet" size="size-4" />
            <span>Paid when you {{ influence() ? 'confirm the post is live' : 'approve all their videos' }}.{{ pay().tiers.length ? ' A creator’s tier is set by their follower count when you hire them.' : '' }}</span>
          </div>
          @if (svc.hired()) {
            <div [class]="bf.infoLock"><mba-icon name="lock" size="size-4" /><span>Creators you’ve hired keep the pay they agreed to.</span></div>
          }
        }
      }
    </mbt-briefing-section>
    }
  `,
  host: { style: 'display: contents' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BriefingPay {
  protected readonly bf = bf;
  protected readonly bars = [1, 2, 3, 4];
  protected readonly num = 'font-(family-name:--font-display) text-[30px] leading-none font-extrabold tabular-nums';
  protected readonly th = 'px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.08em] text-(--fg-3)';
  protected readonly svc = inject(BriefingService);

  readonly flash = input(false);

  protected readonly status = computed(() => sectionStatus('comp', this.svc.brief()!));
  protected readonly influence = computed(() => briefKind(this.svc.brief()!) === 'influence');
  protected readonly pay = computed(() => payView(this.svc.brief()!));
  /** The reward type, matching the old Compensation section's branches. */
  protected readonly kind = computed(() => {
    const brief = this.svc.brief()!;
    if (brief.brief_remuneration_type == null) {
      return brief.enableAffiliateMarketing ? 'affiliate' : 'cash';
    }
    return brief.brief_remuneration_type === 'voucher' && brief.brief_payment_method_id ? 'voucher' : 'plus';
  });
}
